import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import SettingsInput from "./SettingsInput";
import { timezones } from "../../constants/timezones.js";
import { updateBusiness, updateBusinessSettings } from "../../services/profile/profile.service";
import toastr from "toastr";
import SweetAlert from "react-bootstrap-sweetalert";
import SettingsWrapper from "../../common/ScrollableWrapper/SettingsWrapper";
import { deleteBusiness, addOrUpdateBussinessAddress } from "../../services/businesses/businesses.service";
import { deleteMessage } from "../../constants/messages";
import MoveUsers from "../MoveUsers/MoveUsers";
import { showOnDashboard } from "configs/access";
import { userIsAdmin } from "configs/access";
import { useSelector } from "react-redux";
import Autocomplete from "react-google-autocomplete";
import { environment } from "../../configs/environment";

const DEFAULT_TIMEZONE = "America/New_York";
const MAX_NAME_LENGTH = 50;

const GeneralSettings = ({ business, user, plan, showDeleteButton, setSelectedBusiness }) => {
  const { register, handleSubmit, errors } = useForm();
  const [moveUsersShowModal, setMoveUsersShowModal] = useState(false);
  const { account } = useSelector(state => state.account || {});
  const [form, setForm] = useState({
    name: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    dashboardEnabled: false,
    externalId: ""
  });
  const [loading, setLoading] = useState(false);
  const [infoAlert, setInfoAlert] = useState(null);
  const [businessForDelete, setBusinessForDelete] = useState(null);

  const [autoCompleteValue, setAutoCompleteValue] = useState("");
  const [addressForm, setAddressForm] = useState({
    address1: "",
    address2: "",
    city: "",
    state: "",
    postal: "",
    country: "",
    geo: {}
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressShouldSubmit, setAddressShouldSubmit] = useState(false);

  useEffect(() => {
    if (business) {
      setForm({
        name: business.name,
        timezone: business?.settings?.timezone || DEFAULT_TIMEZONE,
        dashboardEnabled: business.dashboardEnabled || false,
        ...(onCheckIfUserIsAdmin() && { externalId: business.externalId || "" })
      });
      setAddressForm({
        address1: business.address?.address1 || "",
        address2: business.address?.address2 || "",
        city: business.address?.city || "",
        state: business.address?.state || "",
        postal: business.address?.postal || "",
        country: business.address?.country || "",
        geo: business.address?.geo || {}
      });
      setAutoCompleteValue("");
    }
    // eslint-disable-next-line
  }, [business]);

  const onSubmit = async data => {
    await updateBusiness(business.businessId, {
      ...{
        name: form.name,
        dashboardEnabled: form.dashboardEnabled || false,
        ...(onCheckIfUserIsAdmin() && { externalId: form.externalId })
      },
      ...data
    });
    await updateBusinessSettings(business.businessId, { ...{ timezone: form.timezone }, ...data });
    toastr.success(`Settings updated`);
  };

  const onAddressSubmit = async data => {
    setAddressLoading(true);
    try {
      const res = await addOrUpdateBussinessAddress(business.businessId, data);
      if (res.success) {
        toastr.success("Address updated!");
      } else {
        throw new Error(res.error || "Something went wrong");
      }
    } catch (e) {
      toastr.error(e.message, "Address didn't updated");
    }
    setAddressLoading(false);
  };

  const onAutoComplete = place => {
    // "street_address" - address1
    // "locality" - city
    // "administrative_area_level_1" - state
    // "postal_code" - postal
    const { address_components, formatted_address, geometry } = place;
    if (!address_components) return;
    if (formatted_address) setAutoCompleteValue(formatted_address);

    const address1 = address_components.find(el => el.types.includes("street_address"));
    const street_number = address_components.find(el => el.types.includes("street_number"));
    const route = address_components.find(el => el.types.includes("route"));
    const city = address_components.find(el => el.types.includes("locality"));
    const state = address_components.find(el => el.types.includes("administrative_area_level_1"));
    const postal = address_components.find(el => el.types.includes("postal_code"));
    const country = address_components.find(el => el.types.includes("country"));

    const geo = {
      lat: geometry.location?.lat?.(),
      lng: geometry.location?.lng?.()
    };

    const data = {
      address1:
        address1?.long_name || ((street_number?.long_name || "") + " " + (route?.short_name || "")).trim() || "",
      address2: addressForm.address2 || "",
      country: country?.short_name || "",
      city: city?.long_name || "",
      state: state?.short_name || state?.long_name || "",
      postal: postal?.long_name || "",
      geo
    };

    setAddressForm(data);
    // for some reasons bussiness id is cached and
    // submit from here always submiting form with primary location bussiness id
    // fixed by "shouldUpdate" var, and setTimemout that garantee that "setForm"
    // complete first, and than form submit
    setTimeout(() => {
      setAddressShouldSubmit(true);
    }, 0);
  };

  useEffect(() => {
    if (addressShouldSubmit) {
      onAddressSubmit(addressForm);
      setAddressShouldSubmit(false);
    }
    // eslint-disable-next-line
  }, [addressShouldSubmit, addressForm]);

  // Also prevent form submiting when press "Enter", because pressing also trigger onAutocomplete
  // and form submitting 2 times
  // it doesn't affect on other components
  const checkKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const hideInfoAlert = () => {
    setBusinessForDelete(null);
    setInfoAlert(null);
  };

  const onChangeFormField = event => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const toggleChecked = event => {
    setForm({ ...form, [event.target.name]: event.target.checked });
  };

  const validateBusinessName = value => {
    return value.length !== 0 && value.length <= MAX_NAME_LENGTH;
  };

  const onShowDashboardEnabledButton = () => {
    return showOnDashboard(plan, user);
  };

  const onCheckIfUserIsAdmin = () => {
    return userIsAdmin(user);
  };

  const onShowDeleteMessage = () => {
    setInfoAlert(deleteMessage("location"));
    setBusinessForDelete(business);
  };

  const onDeleteCurrentLocation = async () => {
    try {
      await deleteBusiness(businessForDelete.businessId);
      hideInfoAlert();
      setSelectedBusiness();
    } catch (error) {
      setInfoAlert(null);
      if (error.message === "409") {
        setMoveUsersShowModal(true);
      }
    }
  };

  const shouldDisplayExternalId = () => {
    return (
      onCheckIfUserIsAdmin() &&
      business &&
      !business.squareLinked &&
      (account.id === "xj5spEZSQb2wd0nFRKY6" || account.id === "IgBNLUJQqJwa1URhwUAX")
    );
  };

  const onCloseMoveUsersModal = () => {
    setMoveUsersShowModal(false);
  };

  const onMoveUsersToAnotherLocation = async businessToMove => {
    setLoading(true);
    await deleteBusiness(businessForDelete.businessId, businessToMove);
    setSelectedBusiness();
    setLoading(false);
    onCloseMoveUsersModal();
  };

  return (
    <>
      {infoAlert && (
        <SweetAlert title={infoAlert.title} onConfirm={hideInfoAlert}>
          {infoAlert.message}
        </SweetAlert>
      )}
      {businessForDelete && infoAlert && (
        <SweetAlert
          title={infoAlert.title}
          showCancel
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={hideInfoAlert}
          onConfirm={onDeleteCurrentLocation}
        >
          {infoAlert.message}
        </SweetAlert>
      )}
      {moveUsersShowModal && (
        <MoveUsers
          closeModal={onCloseMoveUsersModal}
          moveUsers={onMoveUsersToAnotherLocation}
          showMoveUsersModal={moveUsersShowModal}
          loading={loading}
        />
      )}
      <SettingsWrapper
        title={`General ${business ? " - " + business.name : ""}`}
        handleSubmit={handleSubmit(onSubmit)}
        handleDelete={onShowDeleteMessage}
        showDelete={showDeleteButton()}
        buttonStyles={{ maxWidth: 600 }}
        onKeyDown={checkKeyDown}
      >
        <Row>
          <Col md={9}>
            <div className="settings-label address-warning-message">
              {!addressLoading &&
                (business && business.squareLinked
                  ? "Your address can be changed in your Square Dashboard"
                  : !addressForm.address1 ||
                      !addressForm.city ||
                      !addressForm.state ||
                      !addressForm.postal ||
                      !addressForm.country ||
                      !addressForm.geo?.lat
                    ? "You currently do not have an address specified. Please add an address."
                    : "")}
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={9}>
            <SettingsInput
              placeholder={"Enter name"}
              form={form}
              value={form.name}
              enabled={true}
              allowEdit={true}
              name={"name"}
              type={"text"}
              errors={errors}
              label={"Location Name"}
              subtitle={"Your guests may see this name"}
              onChangeValue={onChangeFormField}
              register={register({ required: true, maxLength: 50 })}
              validation={validateBusinessName}
            />
          </Col>
        </Row>
        {business && business.squareLinked && business.squareName && (
          <Row>
            <Col md={9}>
              <SettingsInput
                placeholder={"Enter name"}
                value={business.squareName}
                enabled={true}
                allowEdit={false}
                name={"name"}
                type={"text"}
                errors={errors}
                label={"Square Nickname"}
                subtitle={"Nickname can be changed in your Square Dashboard"}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={9}>
            <div className="flex settings-form-group form-group" style={{ alignItems: "center" }}>
              <div className="settings-label-wrapper">
                <label className="settings-label control-label">Lookup address</label>
              </div>
              <div className="settings-input-wrapper">
                <Autocomplete
                  apiKey={environment.googleMapsKey}
                  onPlaceSelected={onAutoComplete}
                  className="form-control settings-input"
                  language="en"
                  options={{
                    strictBounds: true,
                    componentRestrictions: {
                      country: ["us", "ca"]
                    },
                    types: ["address"],
                    fields: ["address_components", "formatted_address", "geometry"]
                  }}
                  onChange={e => setAutoCompleteValue(e.target.value)}
                  value={autoCompleteValue}
                  disabled={business && business.squareLinked ? true : addressLoading}
                />
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={9}>
            <div className="flex settings-form-group form-group" style={{ alignItems: "center" }}>
              <div className="settings-label-wrapper">
                <label className="settings-label control-label">Address</label>
              </div>
              <div className="settings-input-wrapper">
                {addressForm.address1 && <div>{addressForm.address1}</div>}
                {addressForm.address2 && <div>{addressForm.address2}</div>}
                {addressForm.city && addressForm.state && addressForm.postal && (
                  <div>
                    {addressForm.city}, {addressForm.state} {addressForm.postal}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={9}>
            <SettingsInput
              form={form}
              name={"timezone"}
              type={"select"}
              enabled={true}
              value={form.timezone}
              options={timezones}
              onChangeValue={onChangeFormField}
              errors={errors}
              label={"Timezone"}
              register={null}
            />
          </Col>
        </Row>
        {onShowDashboardEnabledButton() && (
          <Row>
            <Col md={9}>
              <SettingsInput
                form={form}
                name={"dashboardEnabled"}
                type={"switch"}
                enabled={true}
                errors={errors}
                value={form.dashboardEnabled}
                label={"Show on Dashboard"}
                register={null}
                onChangeValue={toggleChecked}
              />
            </Col>
          </Row>
        )}
        {shouldDisplayExternalId() && (
          <>
            <Row>
              <Col md={9}>
                <SettingsInput
                  placeholder={"External ID"}
                  form={form}
                  value={form.externalId}
                  enabled={true}
                  name={"externalId"}
                  type={"text"}
                  errors={errors}
                  label={"External ID"}
                  onChangeValue={onChangeFormField}
                />
              </Col>
            </Row>
            <Row>
              <Col md={9}>
                <SettingsInput
                  placeholder={"Waitly ID"}
                  value={business.businessId}
                  enabled={false}
                  name={"waitlyId"}
                  type={"text"}
                  errors={errors}
                  label={"Waitly ID"}
                />
              </Col>
            </Row>
          </>
        )}
        {business && business.squareLinked && (
          <Row>
            <Col md={9}>
              <SettingsInput
                form={form}
                value={form.externalId}
                enabled={true}
                name={"squareBadge"}
                type={"square-badge"}
                label={"Linked to"}
              />
            </Col>
          </Row>
        )}
      </SettingsWrapper>
    </>
  );
};

export default GeneralSettings;
